import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  userRole: string | null;
  userProfile: { full_name?: string; first_name?: string; last_name?: string; profile_image_url?: string } | null;
  signOut: () => Promise<void>;
  retry: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  error: null,
  userRole: null,
  userProfile: null,
  signOut: async () => {},
  retry: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{ full_name?: string; first_name?: string; last_name?: string; profile_image_url?: string } | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchUserProfile = async (userId: string, email?: string | null) => {
    try {
      // Fetch user profile from profiles table (name/image only, NOT role)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, full_name, profile_image_url')
        .eq('user_id', userId)
        .maybeSingle();

      // Fetch role securely from user_roles table via RPC function
      const { data: roleData, error: roleError } = await supabase
        .rpc('get_user_primary_role', { user_id_param: userId });

      // If profile fetch fails, use email-based fallback for display name
      const profile = profileError || !profileData ? {
        full_name: email?.split('@')[0] || 'User',
        first_name: email?.split('@')[0] || 'User',
        last_name: '',
        profile_image_url: null
      } : {
        full_name: profileData.full_name,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        profile_image_url: profileData.profile_image_url
      };

      // If role fetch fails, set null role (user has no access)
      const role = roleError || !roleData ? null : roleData;

      setUserRole(role);
      setUserProfile(profile);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      // On error, set minimal profile info but NO role (user has no access)
      setUserRole(null);
      setUserProfile({
        full_name: email?.split('@')[0] || 'User',
        first_name: email?.split('@')[0] || 'User',
        last_name: '',
        profile_image_url: null
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    let subscription: any;
    let initTimeoutId: NodeJS.Timeout;
    
    const initializeAuth = async () => {
      try {
        setError(null);
        setLoading(true);

        // Set a reasonable timeout for the entire auth initialization
        initTimeoutId = setTimeout(() => {
          console.warn('Auth initialization timeout - setting loading to false');
          setLoading(false);
        }, 5000); // 5 seconds timeout

        // Set up auth state listener first
        const { data: authData } = supabase.auth.onAuthStateChange(
          (event, session) => {
            
            // Clear init timeout since we got an auth event
            if (initTimeoutId) {
              clearTimeout(initTimeoutId);
              initTimeoutId = null as any;
            }
            
            setSession(session);
            setUser(session?.user ?? null);
            
            if (session?.user) {
              // Use setTimeout to avoid blocking the auth state change
              setTimeout(() => {
                fetchUserProfile(session.user.id, session.user.email);
              }, 0);
            } else {
              setUserRole(null);
              setUserProfile(null);
              setLoading(false);
            }
          }
        );
        
        subscription = authData.subscription;

        // Check for existing session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        // Clear init timeout since we got a response
        if (initTimeoutId) {
          clearTimeout(initTimeoutId);
          initTimeoutId = null as any;
        }
        
        if (sessionError) {
          // Don't throw, just set loading to false and continue
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch user profile for existing session
        if (session?.user) {
          setTimeout(() => {
            fetchUserProfile(session.user.id, session.user.email);
          }, 0);
        } else {
          setLoading(false);
        }
        
      } catch (err: any) {
        if (initTimeoutId) {
          clearTimeout(initTimeoutId);
        }
        setError(err.message || 'Failed to initialize authentication');
        setLoading(false);
      }
    };

    initializeAuth();
    
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      if (initTimeoutId) {
        clearTimeout(initTimeoutId);
      }
    };
  }, [retryCount]); // Re-run when retryCount changes

  const signOut = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        // Handle specific "session missing" error - user is already logged out
        if (error.message.includes('Auth session missing') || error.message.includes('session')) {
          console.log('Session already expired, clearing local state');
        } else {
          console.warn('Other logout error occurred:', error.message);
        }
      } else {
        console.log('Logout successful');
      }
      
    } catch (err: any) {
      console.error('Logout exception:', err);
      // Handle AuthSessionMissingError and other session-related errors
      if (err.name === 'AuthSessionMissingError' || err.message?.includes('session')) {
        console.log('Session missing during logout, clearing local state');
      } else {
        console.warn('Unexpected logout error:', err);
      }
    } finally {
      // Always clear local state regardless of API response
      setUser(null);
      setSession(null);
      setUserRole(null);
      setUserProfile(null);
      setError(null); // Clear any errors to show login form
      setLoading(false);
    }
  };

  const retry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Show error UI if there's a critical auth error
  if (error && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 rounded-full w-fit">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-red-600">Authentication Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {error}
            </p>
            
            <Button 
              onClick={retry}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>

            <p className="text-xs text-muted-foreground">
              If this problem persists, please check your internet connection or contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const value = {
    user,
    session,
    loading,
    error,
    userRole,
    userProfile,
    signOut,
    retry,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};